import { useEffect, useState } from "react";
import { Button, Table } from "react-bootstrap";
import { firebaseApp, fAuth } from "../config/firebase";
import ConfirmDelete from "../components/confirmDelete";
import axios from "axios"; // import { toastMessage } from "../utils/toastMessage";
import { getFunctions } from "firebase/functions";
import { useHttpsCallable } from "react-firebase-hooks/functions";
import { toastMessage } from "../utils/toastMessage";
import { usersQuery } from "../utils/queryHelpers";
import { useCollection } from "react-firebase-hooks/firestore";
import { Loader } from "../hooks/useLoading";
import * as XLSX from "xlsx";
import UserTableModal from "../components/userTableModal";
import { mailType, userStatusTypes } from "../utils/constants";
import SendEmail from "../components/SendEmail";
import {
  getCSuitDoc,
  getFirebaseUsers,
  getUserDoc,
} from "../utils/firestoreHelpers";
import { getDoc, updateDoc } from "firebase/firestore";

const Users = () => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [userData, setUserData] = useState([]);
  const [emailSearch, setEmailSearch] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedId, setSelectedId] = useState(null);
  const [status, setStatus] = useState("Created"); // enum -> ["Created", "Sent Mail","Joined", "Joined & Verified"]
  const [selected, setSelected] = useState(null);
  const [loaderLoading, setLoaderLoading] = useState(false);
  const [isFetchingUsers, setIsFetchingUsers] = useState(false);
  const [membership, setMembership] = useState("Free");

  let filter_names = [
    // "omkarg@iconnectsolutions.com",
    // "vipulw@iconnectsolutions.com",
    "surjan@iconnectsolutions.com",
  ];

  // delete user function hook
  const [deleteUser, deleteExecuting, deleteFunctionError] = useHttpsCallable(
    getFunctions(firebaseApp),
    "deleteUser"
  );

  const [
    verifiedUsersList,
    verifiedUsersListExecuting,
    verifiedUsersListError,
  ] = useHttpsCallable(getFunctions(firebaseApp), "getVerifiedUsersList");

  // get users function hook
  const [snapshot, loading, error] = useCollection(usersQuery(), {
    snapshotListenOptions: {
      includeMetadataChanges: true,
    },
  });

  const formatFirestoreDate = (timestamp, options = {}) => {
    if (!timestamp) return '';

    const {
      timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
      showTimeZoneName = false, 
    } = options;

    const date = timestamp.toDate();

    return date.toLocaleString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZone,
      ...(showTimeZoneName && { timeZoneName: 'short' }),
    });
  };

  let { CREATED, SENT_MAIL, JOINED, JOINED_AND_VERIFIED, DELETED } = userStatusTypes;

  // -- STATUS --
  // created - {status: 'CREATED'}
  // Send mail - {status : 'SENT MAIL'}
  // Joined - {status : 'JOINED' || undefined}

  const getUserFromDb = async () => {
    try {
      setIsFetchingUsers(true);
      let { data } = await handleGetFirebaseUsers();
      let verifiedUserIds = data?.authUsersData
        ?.filter((item) => item.emailVerified || item.phoneNumber !== null)
        .map((item) => item.uid);

      let data1 =
        snapshot?.docs
          ?.map((doc) => {
            const d = { ...doc.data(), id: doc.id };
            return d;
          })
           .filter((user) => {
            if (status === DELETED) {
              return user.deleted === true;
            }

            if (user.deleted) {
              return false;
            }

            if (status === CREATED) return user.status === CREATED;
            if (status === SENT_MAIL) return user.status === SENT_MAIL;
            if (status === JOINED)
              return (
                ["undefined", undefined, JOINED].includes(user.status) &&
                !verifiedUserIds.includes(user.id)
              );
            if (status === JOINED_AND_VERIFIED)
              return (
                ["undefined", undefined, JOINED].includes(user.status) &&
                verifiedUserIds.includes(user.id)
              );
            return false;
          })
          .filter((user) => {
            return user.membership_tier === membership;
          }) || [];

      const toMillis = (v) => {
        if (!v) return 0;
        // Firestore Timestamp
        if (typeof v === "object" && typeof v.seconds === "number") {
          return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
        }
        // JS Date
        if (v instanceof Date) return v.getTime();
        // number millis
        if (typeof v === "number") return v;
        // string date
        const t = Date.parse(String(v));
        return Number.isFinite(t) ? t : 0;
      };

      const sorted = [...data1].sort((a, b) => {
        const aT =
          toMillis(a?.created_at) || toMillis(a?.createdAt) || toMillis(a?.date);
        const bT =
          toMillis(b?.created_at) || toMillis(b?.createdAt) || toMillis(b?.date);
        return bT - aT;
      });

      setUserData(sorted);
    } finally {
      setIsFetchingUsers(false);
    }
  };

  // delete authentication user
  const delete_user = async (uid) => {
    try {
      setLoaderLoading(true);
      const doc = getUserDoc(uid);
      const docData = (await getDoc(doc)).data();
      const newFields = {
        deleted: true,
        added_by: "null",
        deleted_from: "web"
      };
      await updateDoc(doc, newFields); // user collection
      await deleteUser({ userId: uid });

      if (docData.membership_tier === "C-Suite") {
        const cSuiteDoc = getCSuitDoc(docData.email);
        if (cSuiteDoc) {
          await updateDoc(cSuiteDoc, newFields); // c-suite user collection
        }
      }

      toastMessage("Deleted Successfully ", "success");
    } catch (error) {
      console.log("error : ", error);
      toastMessage("Failed to deleted user.", "error");
    } finally {
      setShowEditModal(false);
      setLoaderLoading(false);
    }
  };

  // export users into excel file
  const jsonToExcel = () => {
    let jsonData = JSON.parse(JSON.stringify(userData));
    let requiredUserData = jsonData.map((u, index) => ({ 
      first_name: u.first_name,
      last_name: u.last_name,
      designation: u.designation,
      company: u.company,
      email: u.email,
      dial_code: u.dial_code || u.mobile_number_dial?.replace(/\+/g, "") || "",
      mobile_number: u.mobile_number,
      linkedIn: u.linkedIn,
      promo_code: u.promo_code,
      android_redeem_code: u.android_redeem_code,
    }));

    try {
      // Create a worksheet
      const ws = XLSX.utils.json_to_sheet(requiredUserData);

      // Create a workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sheet1");

      // Generate a Blob containing the Excel file
      const blob = XLSX.writeFile(wb, "IBUsersData.xlsx", {
        compression: true,
      });

      // Create a URL for the Blob
      const blobUrl = URL.createObjectURL(blob);

      // Create a temporary download link
      const downloadLink = document.createElement("a");
      downloadLink.href = blobUrl;
      downloadLink.download = "data.xlsx";

      // Trigger the download
      downloadLink.click();
    } catch (error) {
      console.log(error.message);
    }
  };

  const [showUploadUserModel, setShowUploadUserModel] = useState(false);
  const handleGetFirebaseUsers = async () => {
    try {
      // let userListVerified = await getFirebaseUsers();
      let userListVerified = await verifiedUsersList();
      return userListVerified;
    } catch (error) {
      console.log("error :: ", error);
      return [];
    }
  };
  useEffect(() => { 
    getUserFromDb();

    return () => {};
  }, [status, membership, snapshot]);

  useEffect(() => {
    setCurrentPage(1);
  }, [status, membership, emailSearch]);

  const normalizedNeedle = String(emailSearch || "").trim().toLowerCase();
  const filteredUsers = normalizedNeedle
    ? userData.filter((u) =>
        String(u?.email || "").toLowerCase().includes(normalizedNeedle)
      )
    : userData;

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / rowsPerPage));
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );
  const isLoading = loading || loaderLoading || isFetchingUsers;
  var ROWS_PER_PAGE = 10;

  return (
    <>
      {isLoading && <Loader />}
      <header className="d-flex align-items-center justify-content-between gap-2 mb-3 w-100">
        <h4>Users</h4>
        <div className="d-flex align-items-center gap-4 mb-3">
          <div className="d-flex flex-column gap-0">
            <div>Search email :</div>
            <input
              className="form-control"
              value={emailSearch}
              onChange={(e) => setEmailSearch(e.target.value)}
              placeholder="Search by email"
            />
          </div>
          <div className="d-flex flex-column gap-0">
            <div>Status :</div>
            <select
              className="form-control"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
              }}
            >
              <option value="Created">Created</option>
              <option value="Sent Email">Sent Email</option>
              <option value="Joined">Joined</option>
              <option value="Joined & Verified">Joined & Verified</option>
              {/* <option value="Deleted">Deleted</option> */}
            </select>
          </div>
          <div className="d-flex flex-column gap-0">
            <div>Membership :</div>
            <select
              className="form-control"
              value={membership}
              onChange={(e) => {
                setMembership(e.target.value);
              }}
            >
              <option value="Free">Free</option>
              <option value="Gold">Gold</option>
              {/* <option value="C-Suite">C-Suite</option> */}
            </select>
          </div>
        </div>

        <button
          style={{ width: "max-content" }}
          onClick={() => setShowUploadUserModel(true)}
          className="btn btn-secondary mb-0"
        >
          Add User(s)
        </button>
      </header>
      <div className="d-flex justify-content-between">
        {filteredUsers?.length ? (
          <button onClick={jsonToExcel} className="btn btn-secondary mb-2">
            Export to Excel
          </button>
        ) : null}
      </div>
      {snapshot && snapshot.docs.length ? (
        <table className="table">
          <thead className="table-secondary">
            <tr>
              <th className=""></th>
              <th className="">First Name</th>
              <th className="">Last Name</th>
              <th className="">Designation</th>
              <th className="">Email</th>
              <th className="">Company</th>
              <th className="">Dial code</th>
              <th className="">Contact No</th>
              <th className="">Membership</th>
              {/* <th className="">Last Active</th> */}
              <th className="">{status !== DELETED ? 'Status' : 'Deleted From'}</th>
            </tr>
          </thead>
          <tbody>
            {!isLoading && userData?.length < 1 ? (
              <tr>
                <td colSpan={10} className="text-center">
                  No Data Available
                </td>
              </tr>
            ) : null}
            {paginatedUsers?.map((data, index) => {
              const d = data;
              const { id } = data;
              const lastActiveParts = d.last_active ? formatFirestoreDate(d.last_active).split(" at ") : null;
              return (
                <tr key={id}>
                  <td className="">
                    {(currentPage - 1) * ROWS_PER_PAGE + index + 1}
                  </td>
                  <td className="">{d.first_name}</td>
                  <td className="">{d.last_name}</td>
                  <td className="">{d.designation}</td>
                  <td className="">{d.email}</td>
                  <td className="">{d.company}</td>
                  <td className="">{d.dial_code}</td>
                  <td className="">{d.mobile_number}</td>
                  <td className="">{d.membership_tier}</td>
                  {/* <td className="">
                    {lastActiveParts ? (
                      <>
                        {lastActiveParts[0]}
                        <br />
                        <small className="text-muted">{lastActiveParts[1]}</small>
                      </>
                    ) : (
                      `-`
                    )}
                  </td> */}
                  <td className="">
                    { status === DELETED ? (d.deleted_from !== undefined ? `${d.deleted_from.charAt(0).toUpperCase() + d.deleted_from.slice(1)}` : `-`) : d.status === CREATED ? (
                      <button
                        className="btn btn-primary"
                        style={{ marginLeft: "auto" }}
                        onClick={() => {
                          // setShowEditModal(true);
                          setSelected(d);
                        }}
                      >
                        Send Mail
                      </button>
                    ) : d.status === SENT_MAIL ? (
                      // <button
                      //   className="btn btn-primary"
                      //   style={{ marginLeft: "auto" }}
                      //   onClick={() => {
                      //     // setShowEditModal(true);
                      //     // setSelectedId(d.id);
                      //   }}
                      // >
                      //   Send Mail
                      // </button>
                      <>
                        {/* <button
                          className="btn btn-danger"
                          style={{ marginLeft: "auto" }}
                          onClick={() => {
                            setShowEditModal(true);
                            setSelectedId(d.id);
                          }}
                        >
                          Delete
                        </button> */}
                        {/* <span>Email Sent</span> */}
                        
                      </>
                    ) : [JOINED, undefined, "undefined"].includes(d.status) ? (
                      <button
                        className="btn btn-danger"
                        style={{ marginLeft: "auto" }}
                        onClick={() => {
                          setShowEditModal(true);
                          setSelectedId(d.id);
                        }}
                      >
                        Delete
                      </button>
                    ) : (
                      <span>{d.status}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        !isLoading && <h2>No users to display</h2>
      )}
      {!isLoading ? (
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3 p-3 border rounded bg-light">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="badge text-bg-secondary px-3 py-2">
              Total users: {filteredUsers.length}
            </span>
            <label htmlFor="users-per-page" className="mb-0 fw-semibold">
              Per page:
            </label>
            <select
              id="users-per-page"
              className="form-control form-control-sm"
              style={{ width: "90px" }}
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <span className="text-muted small">
              Showing{" "}
              {filteredUsers.length === 0
                ? 0
                : (currentPage - 1) * rowsPerPage + 1}
              -
              {Math.min(currentPage * rowsPerPage, filteredUsers.length)} of{" "}
              {filteredUsers.length}
            </span>
            <div className="btn-group" role="group" aria-label="Pagination">
              <button
                className="btn btn-outline-primary btn-sm"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </button>
              <button className="btn btn-primary btn-sm" disabled>
                Page {currentPage} / {totalPages}
              </button>
              <button
                className="btn btn-outline-primary btn-sm"
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
              >
                Next
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showEditModal && (
        <ConfirmDelete
          confirmFunction={() => delete_user(selectedId)}
          onClose={() => {
            setShowEditModal(false);
            setSelectedId(null);
          }}
        />
      )}

      {showUploadUserModel ? (
        <UserTableModal onClose={() => setShowUploadUserModel(false)} />
      ) : null}
      {selected !== null ? (
        <SendEmail
          data={selected}
          onClose={() => setSelected(null)}
          type={mailType.WELCOME}
        />
      ) : null}
    </>
  );
};

export default Users;
